import { useMemo } from "react";

import { useAppSelector } from "../../redux/redux";
import { getLinkedFirm } from "../../sessionStorage/sessionStorage";
import { useGetContactCustomersNotesQuery } from "../../slices/ContactNotesSlice";
import { useGetClientFilesQuery } from "../../slices/DocumentsSlice";
import {
  useGetContactEmailsQuery,
  useGetEmailFoldersQuery,
} from "../../slices/EmailsSlice";
import { useFilterTimeQuery } from "../../slices/UserSlice";
import { useGetWorksQuery } from "../../slices/WorkSlice";
import { listCount, TabCount } from "../../utils/tabCounts";
import { defaultTabTimeQueryArgs } from "../time/time-filter.utils";
import { EMPTY_WORKS_FILTERS } from "../works/types/work-filters";
import { WorkTableSort } from "../works/types/work-table";
import { mapWorksFiltersToQuery } from "../works/utils/mapWorksFiltersToQuery";
import { getDefaultContactEmailFolderOption } from "./components/emails/contactEmailFolders";

export type ClientTabCounts = {
  works: TabCount;
  files: TabCount;
  time: TabCount;
  notes: TabCount;
  emails: TabCount;
};

/**
 * Counts for a client's tabs (individual and organization pages, Roadmap
 * 1842). Each count reads the same data as its tab opens with, so it matches
 * the rows the tab shows and refreshes when that data changes. Billing has no
 * count (it mixes invoices and payments; owner, 2026-10-21).
 */
export const useClientTabCounts = (
  customerId: string,
  contactEmail: string,
): ClientTabCounts => {
  const user = useAppSelector((state) => state.appReducer.user);
  const orgId = getLinkedFirm()?.orgId || "";
  const skip = !customerId || !orgId;

  // Same request as the Work tab when it opens (no filters, first page).
  const worksArgs = useMemo(
    () => ({
      ...mapWorksFiltersToQuery(EMPTY_WORKS_FILTERS),
      pageNumber: 1,
      pageSize: 20,
      orgId,
      sortBy: WorkTableSort.StartDate,
      sortOrder: "desc" as "asc" | "desc",
      customerIds: [customerId],
      currentUserId: user?.id,
      hideGreenStatus: false,
    }),
    [customerId, orgId, user?.id],
  );
  const { data: works } = useGetWorksQuery(worksArgs, {
    skip: skip || !user?.id,
  });
  const { data: files } = useGetClientFilesQuery(customerId, { skip });
  const { data: time } = useFilterTimeQuery(
    defaultTabTimeQueryArgs({ orgId, customerId }),
    { skip },
  );
  const { data: notes } = useGetContactCustomersNotesQuery(
    { orgId, customerId, query: "" },
    { skip },
  );

  // The Email tab opens on the Inbox of the signed-in person's mailbox and
  // shows the first page of emails with this client.
  const { data: folders } = useGetEmailFoldersQuery(
    { userId: user?.id || "" },
    { skip: !user?.id },
  );
  const inbox = getDefaultContactEmailFolderOption(folders);
  const { data: emails } = useGetContactEmailsQuery(
    {
      userId: user?.id || "",
      folder: String(inbox?.value || ""),
      pageNumber: undefined,
      contactEmail,
      orgId,
    },
    { skip: !user?.id || !orgId || !contactEmail || !inbox?.value },
  );

  return useMemo(
    () => ({
      works: works ? works[0]?.totalRecords ?? 0 : undefined,
      files: listCount(files?.files),
      time: time?.totalCount,
      notes: listCount(notes),
      // No mailbox connected / no email address: the tab can't list emails.
      emails: listCount(emails?.data, { more: !!emails?.nextPageValue }),
    }),
    [works, files, time, notes, emails],
  );
};
